import React from 'react'
import { formatNumberWithParens, toNumeric } from '@strata-monorepo/utils/phoneNumber'
import useElementClicked from '@strata-monorepo/tracking/src/hooks/useElementClicked'
import { ActionOutcome, ElementType } from '@strata-monorepo/tracking/src/types'

export interface FuseNumberProps {
  additionalClasses?: string
  format?: '(###) ###-####' | '##########'
  fuseName: string
  numberClasses?: string
  /** When provided, wraps the formatted number in a span so callers can style/hide it independently. */
  numberDisplayClasses?: string
  /** Rendered inside the anchor before the number (e.g. an icon or label), keeping a single interactive element. */
  leadingContent?: React.ReactNode
  /** Fired alongside the internal tracking event when the link is activated. */
  onClick?: () => void
  permalease?: string
  testid?: string
  trackingLocation: string
  trackingName?: string
  trackingPosition?: string
  trackingTitle?: string
}

export function FuseNumber({
  additionalClasses,
  format = '(###) ###-####',
  fuseName,
  numberClasses,
  numberDisplayClasses,
  leadingContent,
  onClick,
  permalease,
  testid,
  trackingLocation,
  trackingName,
  trackingPosition,
  trackingTitle,
}: FuseNumberProps): React.ReactElement {
  const trackElementClicked = useElementClicked()

  const phoneNumber = permalease || ''
  const numericPhoneNumber = toNumeric(phoneNumber)
  const formattedPhoneNumber =
    format === '(###) ###-####' ? formatNumberWithParens(phoneNumber) : numericPhoneNumber

  const handleClick = (): void => {
    trackElementClicked({
      clickType: ActionOutcome.Open,
      elementType: ElementType.Link,
      location: trackingLocation,
      itemName: trackingName,
      itemTitle: trackingTitle ?? formattedPhoneNumber,
      itemPosition: trackingPosition ?? '1',
    })
    onClick?.()
  }

  return (
    <span {...(additionalClasses && { className: additionalClasses })}>
      <a
        {...(numberClasses && { className: numberClasses })}
        data-fuse
        data-fuse-format={format}
        data-fuse-name={fuseName}
        data-testid={testid}
        href={`tel:${numericPhoneNumber}`}
        aria-label={`Call ${numericPhoneNumber}`}
        onClick={handleClick}
      >
        {leadingContent}
        {numberDisplayClasses ? (
          <span className={numberDisplayClasses}>{formattedPhoneNumber}</span>
        ) : (
          formattedPhoneNumber
        )}
      </a>
    </span>
  )
}

export default FuseNumber
